import {
  type SystemContext,
  createSystem,
  defaultConfig,
  defineRecipe,
  defineSlotRecipe,
} from '@chakra-ui/react';

/**
 * Recursive color map that allows deep property access like
 * `colors.norm.blue[500]` while still being assignable to `string` at
 * leaf level.
 */
export interface NestedColors {
  [key: string]: NestedColors & string;
  [key: number]: NestedColors & string;
}

/**
 * Builds a nested color object from the flat token dictionary of a Chakra v3
 * system context.
 */
function buildColorsFromTokens(system: SystemContext): NestedColors {
  const flatColors = system.tokens.getCategoryValues('colors');
  const colors: Record<string, unknown> = {};

  for (const path of Object.keys(flatColors)) {
    const parts = path.split('.');
    let current: Record<string, unknown> = colors;

    for (let i = 0; i < parts.length - 1; i++) {
      if (!(parts[i] in current) || typeof current[parts[i]] !== 'object') {
        current[parts[i]] = {};
      }
      current = current[parts[i]] as Record<string, unknown>;
    }

    current[parts[parts.length - 1]] = system.token(`colors.${path}`) as string;
  }

  return colors as NestedColors;
}

export const normThemeConfig = {
  disableLayers: true,
  preflight: false,
  theme: {
    semanticTokens: {
      /**
       * Remap component radius tokens to match Chakra v2 defaults.
       * v3 default: l1=xs, l2=sm(4px), l3=md(6px)
       * v2 default: buttons/inputs used md(6px)
       */
      radii: {
        l1: { value: '{radii.sm}' },
        l2: { value: '{radii.md}' },
        l3: { value: '{radii.lg}' },
      },
      /**
       * Remap color palette semantic tokens to match Chakra v2 behavior.
       * In v2, colorScheme="norm.blue" resolved solid buttons to the 500 shade.
       * In v3, colorPalette="blue" resolves solid to the 600 shade by default.
       * Shifting solid→500 and fg→600 restores v2 visual parity.
       */
      colors: {
        blue: {
          solid: {
            value: { _light: '{colors.blue.500}', _dark: '{colors.blue.200}' },
          },
          fg: {
            value: { _light: '{colors.blue.600}', _dark: '{colors.blue.300}' },
          },
        },
        red: {
          solid: {
            value: { _light: '{colors.red.500}', _dark: '{colors.red.200}' },
          },
          fg: {
            value: { _light: '{colors.red.600}', _dark: '{colors.red.300}' },
          },
        },
        orange: {
          solid: {
            value: {
              _light: '{colors.orange.500}',
              _dark: '{colors.orange.200}',
            },
          },
          fg: {
            value: {
              _light: '{colors.orange.600}',
              _dark: '{colors.orange.300}',
            },
          },
        },
        yellow: {
          solid: {
            value: {
              _light: '{colors.yellow.500}',
              _dark: '{colors.yellow.200}',
            },
          },
          fg: {
            value: {
              _light: '{colors.yellow.600}',
              _dark: '{colors.yellow.300}',
            },
          },
        },
        green: {
          solid: {
            value: {
              _light: '{colors.green.500}',
              _dark: '{colors.green.200}',
            },
          },
          fg: {
            value: {
              _light: '{colors.green.600}',
              _dark: '{colors.green.300}',
            },
          },
        },
        teal: {
          solid: {
            value: { _light: '{colors.teal.500}', _dark: '{colors.teal.200}' },
          },
          fg: {
            value: { _light: '{colors.teal.600}', _dark: '{colors.teal.300}' },
          },
        },
        purple: {
          solid: {
            value: {
              _light: '{colors.purple.500}',
              _dark: '{colors.purple.200}',
            },
          },
          fg: {
            value: {
              _light: '{colors.purple.600}',
              _dark: '{colors.purple.300}',
            },
          },
        },
        pink: {
          solid: {
            value: { _light: '{colors.pink.500}', _dark: '{colors.pink.200}' },
          },
          fg: {
            value: { _light: '{colors.pink.600}', _dark: '{colors.pink.300}' },
          },
        },
      },
    },
    /**
     * Button recipe override to match Chakra v2 sizing and styling.
     * v3 changed sizes, font-sizes, border-width, and outline defaults.
     */
    recipes: {
      button: defineRecipe({
        className: 'chakra-button',
        base: {
          fontWeight: 'semibold',
          focusVisibleRing: 'none',
          _focusVisible: { boxShadow: 'outline' },
        },
        variants: {
          size: {
            xs: { h: '6', minW: '6', textStyle: 'xs', px: '2' },
            sm: { h: '8', minW: '8', textStyle: 'sm', px: '3' },
            md: { h: '10', minW: '10', textStyle: 'md', px: '4' },
            lg: { h: '12', minW: '12', textStyle: 'lg', px: '6' },
          },
          variant: {
            solid: {
              _hover: {
                _disabled: { bg: 'initial' },
              },
            },
            outline: {
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: 'gray.200',
              color: 'gray.800',
              _hover: { bg: 'gray.100' },
              _active: { bg: 'gray.200' },
            },
            ghost: {
              color: 'gray.800',
              _hover: { bg: 'gray.100' },
              _active: { bg: 'gray.200' },
            },
          },
        },
      }),
      /** Input: restore v2 size mapping (md uses textStyle md, not sm) */
      input: defineRecipe({
        className: 'chakra-input',
        base: {
          outline: 'transparent solid 2px',
          outlineOffset: '2px',
        },
        variants: {
          size: {
            xs: { textStyle: 'xs', px: '2', '--input-height': 'sizes.6' },
            sm: { textStyle: 'sm', px: '2.5', '--input-height': 'sizes.8' },
            md: { textStyle: 'md', px: '3', '--input-height': 'sizes.10' },
            lg: { textStyle: 'lg', px: '4', '--input-height': 'sizes.12' },
          },
        },
      }),
      /** Textarea: same sizing fixes as Input */
      textarea: defineRecipe({
        className: 'chakra-textarea',
        base: {
          outline: 'transparent solid 2px',
          outlineOffset: '2px',
        },
        variants: {
          size: {
            xs: { textStyle: 'xs', px: '2' },
            sm: { textStyle: 'sm', px: '2.5' },
            md: { textStyle: 'md', px: '3' },
            lg: { textStyle: 'lg', px: '4' },
          },
        },
      }),
      /**
       * Heading: v2 size variants mapped one step larger than v3.
       * v2: sm→md(16px), md→lg(18px), lg→xl(20px), xl→2xl(24px)
       * v3: sm→sm(14px), md→md(16px), lg→lg(18px), xl→xl(20px)
       */
      heading: defineRecipe({
        className: 'chakra-heading',
        variants: {
          size: {
            sm: { textStyle: 'md' },
            md: { textStyle: 'lg' },
            lg: { textStyle: 'xl' },
            xl: { textStyle: '2xl' },
          },
        },
      }),
      /** Badge: restore v2 font-weight (semibold, not medium) */
      badge: defineRecipe({
        className: 'chakra-badge',
        base: {
          fontWeight: 'semibold',
        },
      }),
      /**
       * Re-add the `chakra-text` class v3 dropped (it ships no `text` recipe)
       * so global shadcn `p` resets keep excluding `<Text>` via `:not([class*='chakra-'])`.
       */
      text: defineRecipe({
        className: 'chakra-text',
        base: {},
      }),
    },
    slotRecipes: {
      /** Dialog: v2 ModalHeader had fontWeight semibold by default; v3 Dialog.Header
       * has no fontWeight. Restore v2 parity. */
      dialog: defineSlotRecipe({
        className: 'chakra-dialog',
        slots: [
          'backdrop',
          'positioner',
          'content',
          'title',
          'description',
          'header',
          'body',
          'footer',
          'closeTrigger',
        ],
        base: {
          header: {
            fontWeight: 'semibold',
          },
        },
      }),
      /**
       * Field: v3 sets alignItems: "flex-start" on vertical orientation,
       * which prevents children (inputs, selects, react-select) from
       * stretching to full width. v2 FormControl was a plain div with
       * no flex alignment, so children naturally took full width.
       */
      field: defineSlotRecipe({
        className: 'chakra-field',
        slots: [
          'root',
          'label',
          'requiredIndicator',
          'errorText',
          'helperText',
        ],
        variants: {
          orientation: {
            vertical: {
              root: {
                flexDirection: 'column',
                alignItems: 'stretch',
              },
            },
          },
        },
      }),
      /** Avatar: restore v2 size mapping (v3 sizes are larger) */
      avatar: defineSlotRecipe({
        className: 'chakra-avatar',
        slots: ['root', 'image', 'fallback', 'icon'],
        variants: {
          size: {
            '2xs': {
              root: {
                '--avatar-size': 'sizes.4',
                '--avatar-font-size': 'fontSizes.2xs',
              },
            },
            xs: {
              root: {
                '--avatar-size': 'sizes.6',
                '--avatar-font-size': 'fontSizes.xs',
              },
            },
            sm: {
              root: {
                '--avatar-size': 'sizes.8',
                '--avatar-font-size': 'fontSizes.sm',
              },
            },
            md: {
              root: {
                '--avatar-size': 'sizes.12',
                '--avatar-font-size': 'fontSizes.md',
              },
            },
            lg: {
              root: {
                '--avatar-size': 'sizes.16',
                '--avatar-font-size': 'fontSizes.lg',
              },
            },
            xl: {
              root: {
                '--avatar-size': '6rem',
                '--avatar-font-size': 'fontSizes.xl',
              },
            },
            '2xl': {
              root: {
                '--avatar-size': '8rem',
                '--avatar-font-size': 'fontSizes.2xl',
              },
            },
          },
        },
      }),
      /** Accordion: remove v3 itemBody padding (v2 had no built-in body padding) */
      accordion: defineSlotRecipe({
        className: 'chakra-accordion',
        slots: [
          'root',
          'item',
          'itemTrigger',
          'itemContent',
          'itemIndicator',
          'itemBody',
        ],
        base: {
          itemBody: {
            pt: '0',
            pb: '0',
          },
          /**
           * v3's default itemTrigger has `borderRadius: var(--accordion-radius)`,
           * which rounds the corners of the expanded bottom border. v2's
           * AccordionButton had no radius (square). Restore square corners.
           */
          itemTrigger: {
            borderRadius: '0',
          },
        },
      }),
      /** Checkbox: restore v2 sizing (md=16px) and blue checked state */
      checkbox: defineSlotRecipe({
        className: 'chakra-checkbox',
        slots: ['root', 'label', 'control', 'indicator', 'group'],
        base: {
          control: {
            borderWidth: '2px',
            borderColor: 'border',
          },
          label: {
            fontWeight: 'normal',
          },
        },
        variants: {
          size: {
            sm: { control: { boxSize: '3' } },
            md: { control: { boxSize: '4', p: '0' } },
            lg: { control: { boxSize: '5', p: '0' } },
          },
          variant: {
            solid: {
              control: {
                '&:is([data-state=checked], [data-state=indeterminate])': {
                  bg: 'norm.blue.500',
                  borderColor: 'norm.blue.500',
                  color: 'white',
                },
              },
            },
            outline: {
              control: {
                '&:is([data-state=checked], [data-state=indeterminate])': {
                  borderColor: 'norm.blue.500',
                  color: 'norm.blue.500',
                },
              },
            },
          },
        },
      }),
      /** NumberInput: hide stepper arrows (v2 didn't show them) */
      numberInput: defineSlotRecipe({
        className: 'chakra-number-input',
        slots: [
          'root',
          'label',
          'input',
          'control',
          'valueText',
          'incrementTrigger',
          'decrementTrigger',
          'scrubber',
        ],
        base: {
          input: {
            pe: '2',
          },
          control: {
            display: 'none',
          },
        },
      }),
      /** Switch: v2 defaulted to blue track color */
      switch: defineSlotRecipe({
        className: 'chakra-switch',
        slots: ['root', 'track', 'thumb', 'label', 'indicator', 'control'],
        defaultVariants: {
          variant: 'solid',
          colorPalette: 'blue',
        },
        base: {
          label: {
            fontWeight: 'normal',
          },
        },
        variants: {
          variant: {
            solid: {
              control: {
                borderRadius: 'full',
                bg: 'bg.emphasized',
                focusVisibleRing: 'outside',
                _checked: {
                  bg: '{colors.blue.500}',
                },
              },
              thumb: {
                bg: 'white',
                width: 'var(--switch-height)',
                height: 'var(--switch-height)',
                scale: '0.8',
                boxShadow: 'sm',
                _checked: {
                  bg: 'white',
                },
              },
            },
          },
        },
      }),
      /** Menu: restore v2 content min-width */
      menu: defineSlotRecipe({
        className: 'chakra-menu',
        slots: [
          'arrow',
          'arrowTip',
          'content',
          'contextTrigger',
          'indicator',
          'item',
          'itemGroup',
          'itemGroupLabel',
          'itemIndicator',
          'itemText',
          'positioner',
          'separator',
          'trigger',
          'triggerItem',
          'itemCommand',
        ],
        base: {
          content: {
            minW: '8rem',
          },
          item: {
            cursor: 'pointer',
          },
        },
      }),
      /** RadioGroup: restore v2 font-weight */
      radioGroup: defineSlotRecipe({
        className: 'chakra-radio-group',
        slots: [
          'root',
          'label',
          'item',
          'itemText',
          'itemControl',
          'indicator',
          'itemAddon',
        ],
        base: {
          item: {
            fontWeight: 'normal',
          },
          itemControl: {
            borderWidth: '2px',
            borderColor: 'border',
          },
        },
      }),
    },
    tokens: {
      colors: {
        blue: {
          50: { value: '#EEEBFF' },
          100: { value: '#CEC3FF' },
          200: { value: '#AF9CFF' },
          300: { value: '#6F4EFF' },
          400: { value: '#3F13FF' },
          500: { value: '#2800D7' },
          600: { value: '#2100B0' },
          700: { value: '#190089' },
          800: { value: '#120061' },
          900: { value: '#0B003A' },
        },
        red: {
          50: { value: '#FFEEEB' },
          100: { value: '#FFDED7' },
          200: { value: '#FFBFB0' },
          300: { value: '#FF9F89' },
          400: { value: '#FF4F26' },
          500: { value: '#FE2F00' },
          600: { value: '#D72800' },
          700: { value: '#B02100' },
          800: { value: '#891900' },
          900: { value: '#611200' },
        },
        orange: {
          50: { value: '#FFFAF0' },
          100: { value: '#FEEBCB' },
          200: { value: '#FBD38D' },
          300: { value: '#F6AD55' },
          400: { value: '#ED8936' },
          500: { value: '#DD6B20' },
          600: { value: '#C05621' },
          700: { value: '#9C4221' },
          800: { value: '#7B341E' },
          900: { value: '#652B19' },
        },
        yellow: {
          50: { value: '#FFF9EB' },
          100: { value: '#FFF3D7' },
          200: { value: '#FFE6B0' },
          300: { value: '#FFDA89' },
          400: { value: '#FFC84E' },
          500: { value: '#FEAF00' },
          600: { value: '#D79400' },
          700: { value: '#975A16' },
          800: { value: '#895E00' },
          900: { value: '#4E3500' },
        },
        green: {
          50: { value: '#EBFFF4' },
          100: { value: '#D7FFE8' },
          200: { value: '#B0FFD2' },
          300: { value: '#9CFFC7' },
          400: { value: '#00FE6F' },
          500: { value: '#00D75E' },
          600: { value: '#00B04D' },
          700: { value: '#00893C' },
          800: { value: '#00612B' },
          900: { value: '#003A19' },
        },
        teal: {
          50: { value: '#DFFCF6' },
          100: { value: '#A9F7E8' },
          200: { value: '#85F4DE' },
          300: { value: '#61F0D5' },
          400: { value: '#19EAC2' },
          500: { value: '#13C9A6' },
          600: { value: '#11B797' },
          700: { value: '#0E937A' },
          800: { value: '#074C3E' },
          900: { value: '#042821' },
        },
        purple: {
          50: { value: '#F9EBFF' },
          100: { value: '#F3D7FF' },
          200: { value: '#E6B0FF' },
          300: { value: '#D475FF' },
          400: { value: '#C23AFF' },
          500: { value: '#AF00FE' },
          600: { value: '#9400D7' },
          700: { value: '#7900B0' },
          800: { value: '#5E0089' },
          900: { value: '#430061' },
        },
        gray: {
          50: { value: '#F0F0F3' },
          100: { value: '#E5E6EA' },
          200: { value: '#DADCE1' },
          300: { value: '#BABDC6' },
          400: { value: '#A5A8B5' },
          500: { value: '#8F93A3' },
          600: { value: '#5E6272' },
          700: { value: '#32343C' },
          800: { value: '#292A32' },
          900: { value: '#292A32' },
        },
        norm: {
          black: { value: '#000000' },
          normBlack: { value: '#17181C' },
          normWhite: { value: '#FBFBFB' },
          whiteAlpha: {
            50: { value: 'rgba(255, 255, 255, 0.04)' },
            100: { value: 'rgba(255, 255, 255, 0.06)' },
            200: { value: 'rgba(255, 255, 255, 0.08)' },
            300: { value: 'rgba(255, 255, 255, 0.16)' },
            400: { value: 'rgba(255, 255, 255, 0.24)' },
            500: { value: 'rgba(255, 255, 255, 0.36)' },
            600: { value: 'rgba(255, 255, 255, 0.48)' },
            700: { value: 'rgba(255, 255, 255, 0.64)' },
            800: { value: 'rgba(255, 255, 255, 0.80)' },
            900: { value: 'rgba(255, 255, 255, 0.92)' },
          },
          blackAlpha: {
            50: { value: 'rgba(0, 0, 0, 0.04)' },
            100: { value: 'rgba(0, 0, 0, 0.06)' },
            200: { value: 'rgba(0, 0, 0, 0.08)' },
            300: { value: 'rgba(0, 0, 0, 0.16)' },
            400: { value: 'rgba(0, 0, 0, 0.24)' },
            500: { value: 'rgba(0, 0, 0, 0.36)' },
            600: { value: 'rgba(0, 0, 0, 0.48)' },
            700: { value: 'rgba(0, 0, 0, 0.64)' },
            800: { value: 'rgba(0, 0, 0, 0.80)' },
            900: { value: 'rgba(0, 0, 0, 0.92)' },
          },
          gray: {
            50: { value: '#F0F0F3' },
            100: { value: '#E5E6EA' },
            200: { value: '#DADCE1' },
            300: { value: '#BABDC6' },
            400: { value: '#A5A8B5' },
            500: { value: '#8F93A3' },
            600: { value: '#5E6272' },
            700: { value: '#32343C' },
            800: { value: '#292A32' },
            900: { value: '#292A32' },
          },
          orange: {
            50: { value: '#FFFAF0' },
            100: { value: '#FEEBCB' },
            200: { value: '#FBD38D' },
            300: { value: '#F6AD55' },
            400: { value: '#ED8936' },
            500: { value: '#DD6B20' },
            600: { value: '#C05621' },
            700: { value: '#9C4221' },
            800: { value: '#7B341E' },
            900: { value: '#652B19' },
          },
          red: {
            50: { value: '#FFEEEB' },
            100: { value: '#FFDED7' },
            200: { value: '#FFBFB0' },
            300: { value: '#FF9F89' },
            400: { value: '#FF4F26' },
            500: { value: '#FE2F00' },
            600: { value: '#D72800' },
            700: { value: '#B02100' },
            800: { value: '#891900' },
            900: { value: '#611200' },
          },
          yellow: {
            50: { value: '#FFF9EB' },
            100: { value: '#FFF3D7' },
            200: { value: '#FFE6B0' },
            300: { value: '#FFDA89' },
            400: { value: '#FFC84E' },
            500: { value: '#FEAF00' },
            600: { value: '#D79400' },
            700: { value: '#975A16' },
            800: { value: '#895E00' },
            900: { value: '#4E3500' },
          },
          green: {
            50: { value: '#EBFFF4' },
            100: { value: '#D7FFE8' },
            200: { value: '#B0FFD2' },
            300: { value: '#9CFFC7' },
            400: { value: '#00FE6F' },
            500: { value: '#00D75E' },
            600: { value: '#00B04D' },
            700: { value: '#00893C' },
            800: { value: '#00612B' },
            900: { value: '#003A19' },
          },
          teal: {
            50: { value: '#DFFCF6' },
            100: { value: '#A9F7E8' },
            200: { value: '#85F4DE' },
            300: { value: '#61F0D5' },
            400: { value: '#19EAC2' },
            500: { value: '#13C9A6' },
            600: { value: '#11B797' },
            700: { value: '#0E937A' },
            800: { value: '#074C3E' },
            900: { value: '#042821' },
          },
          blue: {
            50: { value: '#EEEBFF' },
            100: { value: '#CEC3FF' },
            200: { value: '#AF9CFF' },
            300: { value: '#6F4EFF' },
            400: { value: '#3F13FF' },
            500: { value: '#2800D7' },
            600: { value: '#2100B0' },
            700: { value: '#190089' },
            800: { value: '#120061' },
            900: { value: '#0B003A' },
          },
          purple: {
            50: { value: '#F9EBFF' },
            100: { value: '#F3D7FF' },
            200: { value: '#E6B0FF' },
            300: { value: '#D475FF' },
            400: { value: '#C23AFF' },
            500: { value: '#AF00FE' },
            600: { value: '#9400D7' },
            700: { value: '#7900B0' },
            800: { value: '#5E0089' },
            900: { value: '#430061' },
          },
        },
      },
      /**
       * Chakra v2 mapped numeric border tokens (e.g. `border="1px"`) to
       * `1px solid`. Chakra v3 only ships named tokens (sm/md/...), so bare
       * numeric borders would render width-only (invisible). Restore the v2
       * numeric tokens so existing `border="1px"`/`_expanded={{ borderBottom:
       * '1px' }}` usages keep their solid borders.
       */
      borders: {
        '1px': { value: '1px solid' },
        '2px': { value: '2px solid' },
        '4px': { value: '4px solid' },
        '8px': { value: '8px solid' },
      },
    },
  },
};

const system = createSystem(defaultConfig, normThemeConfig);

export const themeColors = buildColorsFromTokens(system);

export type ThemeWithColors = SystemContext & { colors: NestedColors };

export const baseTheme: ThemeWithColors = Object.assign(system, {
  colors: themeColors,
}) as ThemeWithColors;

const AVATAR_COLOR_PALETTES = [
  'red',
  'orange',
  'yellow',
  'green',
  'teal',
  'blue',
  'cyan',
  'purple',
  'pink',
] as const;

/**
 * Maps a name string to a consistent Chakra colorPalette value,
 * reproducing the auto-coloring behavior from Chakra v2's Avatar.
 */
export function getAvatarColorPalette(
  name: string | undefined | null,
): (typeof AVATAR_COLOR_PALETTES)[number] | 'gray' {
  if (!name) return 'gray';
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLOR_PALETTES[Math.abs(hash) % AVATAR_COLOR_PALETTES.length];
}

export function getNormColorPalette(colorKey: string): NestedColors {
  return (baseTheme.colors.norm[colorKey] ||
    baseTheme.colors.norm.gray) as NestedColors;
}
